import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { useAssessment } from '../../../hooks/useAssessment';
import { NIST_OUTCOMES, NIST_FUNCTIONS } from '../../../utils/nistAiRmf';
import { CheckCircle, AlertTriangle, XCircle, FileText, ArrowRight } from 'lucide-react';
export function NistGapAnalysis() {
  const navigate = useNavigate();
  const {
    assessment
  } = useAssessment();
  // Helper to get status for a single outcome
  const getOutcomeStatus = (outcomeId: string) => {
    const linkedEvidence = assessment.evidence.filter(e => e.linkedObligations.includes(outcomeId));
    if (linkedEvidence.length >= 2) return 'aligned';
    if (linkedEvidence.length === 1) return 'partial';
    return 'not-evidenced';
  };
  // Calculate stats per function
  const functionStats = NIST_FUNCTIONS.map(func => {
    const outcomes = NIST_OUTCOMES.filter(o => o.function === func.id);
    const total = outcomes.length;
    const aligned = outcomes.filter(o => getOutcomeStatus(o.id) === 'aligned').length;
    const partial = outcomes.filter(o => getOutcomeStatus(o.id) === 'partial').length;
    const coverage = Math.round((aligned + partial * 0.5) / total * 100);
    return {
      ...func,
      total,
      aligned,
      partial,
      coverage
    };
  });
  // Overall stats
  const totalOutcomes = NIST_OUTCOMES.length;
  const totalAligned = NIST_OUTCOMES.filter(o => getOutcomeStatus(o.id) === 'aligned').length;
  const totalPartial = NIST_OUTCOMES.filter(o => getOutcomeStatus(o.id) === 'partial').length;
  const totalGaps = NIST_OUTCOMES.filter(o => getOutcomeStatus(o.id) === 'not-evidenced').length;
  const overallCoverage = Math.round((totalAligned + totalPartial * 0.5) / totalOutcomes * 100);
  return <div className="max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-477">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4" data-id="element-478">
        <div data-id="element-479">
          <h1 className="text-3xl font-bold mb-3" data-id="element-480">NIST AI RMF Analysis</h1>
          <p className="text-slate-400 text-lg" data-id="element-481">
            Risk management maturity assessment across Govern, Map, Measure, and
            Manage functions.
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-482">
          <Button variant="secondary" onClick={() => navigate('/assessment/audit-trail')} data-id="element-483">
            View Audit Trail
          </Button>
          <Button onClick={() => window.print()} data-id="element-484">Export Report</Button>
        </div>
      </div>

      {/* Function Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10" data-id="element-485">
        {functionStats.map(stat => <Card key={stat.id} className="relative overflow-hidden" data-id="element-486">
            <div className="absolute top-0 left-0 w-1 h-full bg-teal-500" style={{
          opacity: stat.coverage / 100
        }} data-id="element-487" />
            <div className="text-sm text-slate-400 mb-1" data-id="element-488">
              {stat.title} Function
            </div>
            <div className="text-3xl font-bold text-white mb-2" data-id="element-489">
              {stat.coverage}%
            </div>
            <div className="w-full bg-navy-900 h-1.5 rounded-full overflow-hidden" data-id="element-490">
              <div className="bg-teal-500 h-full" style={{
            width: `${stat.coverage}%`
          }} data-id="element-491" />
            </div>
          </Card>)}
      </div>

      {/* Detailed Breakdown by Function */}
      <div className="space-y-12" data-id="element-492">
        {NIST_FUNCTIONS.map(func => <div key={func.id} data-id="element-493">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center" data-id="element-494">
              <span className="bg-teal-500/10 text-teal-500 px-3 py-1 rounded text-lg mr-3 border border-teal-500/20" data-id="element-495">
                {func.title}
              </span>
              <span className="text-slate-400 text-lg font-normal" data-id="element-496">
                {func.description}
              </span>
            </h2>

            <div className="grid grid-cols-1 gap-4" data-id="element-497">
              {NIST_OUTCOMES.filter(o => o.function === func.id).map(outcome => {
            const status = getOutcomeStatus(outcome.id);
            const evidenceCount = assessment.evidence.filter(e => e.linkedObligations.includes(outcome.id)).length;
            return <Card key={outcome.id} className={`border-l-4 ${status === 'aligned' ? 'border-l-teal-500' : status === 'partial' ? 'border-l-amber-500' : 'border-l-red-500'}`} data-id="element-498">
                      <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-499">
                        <div className="flex-1" data-id="element-500">
                          <div className="flex items-center space-x-3 mb-2" data-id="element-501">
                            <span className="font-mono text-sm text-slate-500" data-id="element-502">
                              {outcome.id}
                            </span>
                            {status === 'not-evidenced' && <Badge variant="red" className="bg-red-500/10 text-red-400 border-red-500/20" data-id="element-503">
                                Gap Identified
                              </Badge>}
                          </div>
                          <h3 className="text-lg font-semibold text-white mb-2" data-id="element-504">
                            {outcome.title}
                          </h3>
                          <p className="text-slate-400 mb-3" data-id="element-505">
                            {outcome.description}
                          </p>

                          {outcome.subcategories && <div className="flex flex-wrap gap-2 mt-2" data-id="element-506">
                              {outcome.subcategories.map(sub => <span key={sub} className="text-xs text-slate-500 bg-navy-900 px-2 py-1 rounded border border-slate-800" data-id="element-507">
                                  {sub}
                                </span>)}
                            </div>}
                        </div>

                        <div className="flex flex-col items-end space-y-4 min-w-[140px]" data-id="element-508">
                          <div className="flex items-center space-x-2" data-id="element-509">
                            {status === 'aligned' && <CheckCircle className="h-5 w-5 text-teal-500" data-id="element-510" />}
                            {status === 'partial' && <AlertTriangle className="h-5 w-5 text-amber-500" data-id="element-511" />}
                            {status === 'not-evidenced' && <XCircle className="h-5 w-5 text-red-500" data-id="element-512" />}
                            <span className={`font-medium capitalize ${status === 'aligned' ? 'text-teal-400' : status === 'partial' ? 'text-amber-400' : 'text-red-400'}`} data-id="element-513">
                              {status === 'not-evidenced' ? 'Not Evidenced' : status}
                            </span>
                          </div>

                          <div className="text-sm text-slate-500 flex items-center" data-id="element-514">
                            <FileText className="h-4 w-4 mr-2" data-id="element-515" />
                            {evidenceCount} Evidence Items
                          </div>

                          {status !== 'aligned' && <Button size="sm" variant="ghost" className="text-teal-400 hover:text-teal-300 p-0" onClick={() => navigate(`/assessment/nist/${func.id.toLowerCase()}`)} data-id="element-516">
                              Add Evidence{' '}
                              <ArrowRight className="h-3 w-3 ml-1" data-id="element-517" />
                            </Button>}
                        </div>
                      </div>
                    </Card>;
          })}
            </div>
          </div>)}
      </div>
    </div>;
}